import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileHeader } from './MobileHeader';
import { MobileNavigation } from './MobileNavigation';
import { MobileChatInput } from './MobileChatInput';
import { MobileCentralInterface } from './MobileCentralInterface';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { getRouteForSection } from '@/utils/moduleMapping';

// Get route for a mobile section using centralized moduleMapping
const getMobileRouteForSection = (section: string): string => {
  // Handle special cases first
  if (section === 'settings' || section === 'dashboard') {
    return '/dashboard';
  }

  return getRouteForSection(section);
};

import { Interface } from '@/components/dashboard/Interface';

// Import mobile wrappers
import { MobileKnowledge } from './modules/MobileKnowledge';
import { MobileVendorPortal } from './modules/MobileVendorPortal';
import { MobileFinance } from './modules/MobileFinance';
import { MobileFieldOperations } from './modules/MobileFieldOperations';
import { MobileUserManagement } from './modules/MobileUserManagement';
import { MobileKnowledgeAdmin } from './modules/MobileKnowledgeAdmin';
import { MobileUserProfile } from './modules/MobileUserProfile';
import { MobileCompanyBranding } from './modules/MobileCompanyBranding';

// KB admin pages (responsive - work on mobile)
import KBUploadPage from '@/pages/admin/KBUploadPage';
import KBCloudImportPage from '@/pages/admin/KBCloudImportPage';
import KBReviewQueuePage from '@/pages/admin/KBReviewQueuePage';
import KBDocumentsPage from '@/pages/admin/KBDocumentsPage';
import KBEntityTypesPage from '@/pages/admin/KBEntityTypesPage';
import KBEntityQAPage from '@/pages/admin/KBEntityQAPage';

import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { getApiEndpoint, getImageURL } from '@/utils/ERPUtils';
import axios from 'axios';
import { useKnowledgeBaseChat } from '@/hooks/useKnowledgeBaseChat';
import { triggerNewChatAdded } from '@/hooks/useYourChats';

import { logger } from '@/utils/logger';
import { SaveToKBModal } from '@/components/ui/SaveToKBModal';

export const MobileLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [chatSessionId, setChatSessionId] = useState<string | null>(searchParams.get('session_id'));
  const [chatTitle, setChatTitle] = useState<string>('New Chat');
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  // Get user's name from auth context (actual logged-in user)
  const getCurrentUserName = () => {
    // Prioritize first_name from auth user
    if (user?.first_name) {
      return user.first_name;
    }
    // Fallback to full_name (take first part)
    if (user?.full_name) {
      return user.full_name.split(' ')[0];
    }
    // Fallback to name field
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    // Fallback to email username
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };
  
  const getCurrentRole = () => {
    return 'Executive'; // Default role for mobile interface
  };
  
  const [showNavigation, setShowNavigation] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    content: string;
    role: 'user' | 'assistant';
    timestamp: Date;
    images?: string[];
    videoUrl?: string;
    isComplete?: boolean;
    wasStreamed?: boolean; // Flag to indicate content was already streamed from API
    sources?: Array<{
      title?: string;
      url?: string;
      file_name?: string;
      file_type?: string;
      relevance_score?: number;
      snippet?: string;
      qdrant_id?: string;
    }>;
    citations?: Array<{
      id: string;
      name: string;
      description: string;
      url: string;
    }>;
    feedbackGiven?: 'thumbs_up' | 'thumbs_down' | null;
  }>>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [micBlocked, setMicBlocked] = useState(false);
  const recognitionRef = React.useRef<any>(null);
  const [isChatMode, setIsChatMode] = useState(false);
  const [currentChatTitle, setCurrentChatTitle] = useState('');
  const [saveToKBContent, setSaveToKBContent] = useState<string | null>(null);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(() => {
    const path = window.location.pathname;
    if (path === '/dashboard/knowledge') return 'knowledge';
    if (path.startsWith('/dashboard/admin')) return 'dashboard/admin';
    return 'dashboard';
  });
  const [activeAgent, setActiveAgent] = useState('');

  // Feature states from desktop version
  const [isKnowledgeMode, setIsKnowledgeMode] = useState(true);

  // Knowledge Base chat hook - shared between desktop and mobile
  const { streamMessage: streamKnowledgeBaseMessage } = useKnowledgeBaseChat({
    onMessageUpdate: (messageId, updates) => {
      setChatMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, ...updates }
          : msg
      ));
    },
    onStreamComplete: () => {
      setIsTyping(false);
    },
    onError: (error?: Error) => {
      toast.error(error?.message || 'Failed to connect to Knowledge Base');
    },
    onSessionId: (newSessionId, sessionTitle) => {
      // Update session_id in URL when received from KB chat
      if (newSessionId && newSessionId !== chatSessionId) {
        const isNewSession = !chatSessionId;
        setChatSessionId(newSessionId);
        setSearchParams({ session_id: newSessionId }, { replace: true });

        // Update header title immediately
        if (sessionTitle) {
          setChatTitle(sessionTitle);
        }

        // Notify sidebar about new chat (only on first message)
        if (isNewSession) {
          triggerNewChatAdded({
            sessionId: newSessionId,
            sessionTitle: sessionTitle || 'New Chat',
            category: 'General',
            startedAt: new Date().toISOString(),
          });
        }
      }
    },
    sessionId: chatSessionId, // Pass current session_id for subsequent messages
  });

  // Function to set and get chat input value
  const setChatInputValue = React.useRef<{
    get: () => string;
    set: (value: string) => void;
  }>({
    get: () => '',
    set: () => {}
  });

  // Function to load chat session from API
  const loadChatSession = async (sessionId: string) => {
    setIsLoadingSession(true);
    try {
      const response = await axios.post(
        getApiEndpoint('GET_DESIGN_CHAT_SESSION'),
        { session_id: sessionId },
        { withCredentials: true }
      );

      if (response.data?.message?.success === false) {
        logger.error('Failed to load chat session:', response.data.message);
        toast.error('Chat not found');
        // Clear session from URL and reset state
        setChatSessionId(null);
        setSearchParams({}, { replace: true });
        setChatMessages([]);
        setChatTitle('New Chat');
        return;
      }

      const sessionData = response.data?.message;

      // Set chat title (API returns session_title)
      if (sessionData?.session_title) {
        setChatTitle(sessionData.session_title);
      } else if (sessionData?.title) {
        setChatTitle(sessionData.title);
      }

      // Load chat messages with images, video, sources, citations
      if (sessionData?.messages && Array.isArray(sessionData.messages)) {
        let loadedMessages = sessionData.messages.map((msg: any, index: number) => {
          // Parse sources - API returns double-encoded JSON string
          let parsedSources = undefined;
          if (msg.sources) {
            try {
              // Handle double-encoded JSON: "{\"sources\": [...]}"
              const sourcesData = typeof msg.sources === 'string'
                ? JSON.parse(msg.sources)
                : msg.sources;
              // Extract inner sources array if nested
              let rawSources = sourcesData?.sources || sourcesData;
              // Ensure it's an array and transform to expected format
              if (rawSources && Array.isArray(rawSources)) {
                // Transform API format to KnowledgeBaseSourceItem format
                parsedSources = rawSources.map((s: any) => ({
                  title: s.title,
                  file_name: s.file_name,
                  relevance_score: s.score ?? s.relevance_score,
                  snippet: s.excerpt ?? s.snippet,
                  qdrant_id: s.document_id ?? s.qdrant_id,
                }));
              }
            } catch (e) {
              logger.warn('Failed to parse sources:', e);
            }
          }

          return {
            id: msg.name || msg.id || `msg_${index}`,
            content: msg.content || msg.message || msg.text || '',
            role: msg.role as 'user' | 'assistant',
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            images: msg.images && msg.images.length > 0 ? msg.images : undefined,
            videoUrl: msg.video_url || undefined,
            isComplete: true, // Loaded messages are always complete
            sources: parsedSources,
            citations: msg.citations || undefined
          };
        });

        // Fetch existing feedback for this session and merge into messages
        try {
          const fbRes = await axios.post(
            getApiEndpoint('GET_SESSION_FEEDBACK'),
            { session_id: sessionId },
            { withCredentials: true }
          );
          const feedbackMap: Record<number, string> = fbRes.data?.message?.feedback_map || {};
          if (Object.keys(feedbackMap).length > 0) {
            let assistantIdx = 0;
            loadedMessages = loadedMessages.map((m: any) => {
              if (m.role === 'assistant') {
                const fb = feedbackMap[assistantIdx];
                assistantIdx++;
                if (fb) return { ...m, feedbackGiven: fb };
              }
              return m;
            });
          }
        } catch {
          // Non-critical - feedback indicators just won't show
        }

        setChatMessages(loadedMessages);
      }
    } catch (error) {
      logger.error('Error loading chat session:', error);
    } finally {
      setIsLoadingSession(false);
    }
  };

  // Load chat session from URL on page load/refresh or when session_id changes
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Set chat mode immediately when session ID is in URL
      setIsChatMode(true);
      // Load session if it's different OR if messages are empty (initial load)
      if (sessionId !== chatSessionId || chatMessages.length === 0) {
        setChatSessionId(sessionId);
        loadChatSession(sessionId);
      }
    } else if (chatSessionId && !sessionId) {
      // URL no longer has session_id, reset to new chat state
      setChatSessionId(null);
      setChatMessages([]);
      setChatTitle('New Chat');
      setIsChatMode(false);
    }
  }, [searchParams]);

  // Keyboard detection for mobile
  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const screenHeight = window.screen.height;
        const threshold = screenHeight * 0.75; // If viewport is less than 75% of screen, keyboard is likely open
        
        setKeyboardVisible(currentHeight < threshold);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    } else {
      // Fallback for browsers without Visual Viewport API
      const handleResize = () => {
        const currentHeight = window.innerHeight;
        const screenHeight = window.screen.height;
        const threshold = screenHeight * 0.75;
        
        setKeyboardVisible(currentHeight < threshold);
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Chat state management - Real implementation matching desktop version
  const handleChatSubmit = async (message: string) => {
    if (!message.trim()) return;
    if (isTyping) return; // Block sends while a response is in flight

    // Enable chat mode when user submits from landing view (like desktop)
    if (!isChatMode) {
      setIsChatMode(true);
    }

    // Convert selected image to base64 (matching desktop behavior)
    const imageFile = selectedFiles.find(file => file.type.startsWith('image/'));
    let base64Image: string | undefined;
    let localPreviewUrl: string | undefined;
    if (imageFile) {
      const reader = new FileReader();
      base64Image = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });
      localPreviewUrl = base64Image;
    }

    // Create assistant message ID outside try block for error handling access
    const assistantMessageId = (Date.now() + 1).toString();

    try {
      // Create user message (include image preview if present)
      const userMessage: {
        id: string;
        content: string;
        role: 'user';
        timestamp: Date;
        images?: string[];
      } = {
        id: Date.now().toString(),
        content: message.trim(),
        role: 'user' as const,
        timestamp: new Date()
      };
      if (localPreviewUrl) {
        userMessage.images = [localPreviewUrl];
      }

      // Create assistant message placeholder (like desktop)
      const assistantPlaceholder = {
        id: assistantMessageId,
        content: '',
        role: 'assistant' as const,
        timestamp: new Date(),
        isComplete: false
      };

      // Add user message and assistant placeholder to chat
      const updatedMessages = [...chatMessages, userMessage];
      setChatMessages([...updatedMessages, assistantPlaceholder]);

      // Clear the input and selected files immediately
      if (setChatInputValue.current) {
        setChatInputValue.current.set('');
      }
      setSelectedFiles([]);

      // Show typing indicator
      setIsTyping(true);

      let response;

      if (isKnowledgeMode) {
        await streamKnowledgeBaseMessage(message.trim(), assistantMessageId);
        return; // Exit early for streaming response
      } else {
        // Call ERP Gemini chat API
        try {
          const chatResponse = await axios.post(
            getApiEndpoint('CHAT_CONVERSATION'),
            {
              message: message.trim(),
              session_id: chatSessionId || undefined,
              ...(base64Image ? { image: base64Image } : {})
            },
            { withCredentials: true }
          );

          // Check for error response
          if (chatResponse.data?.message?.success === false) {
            const err = new Error(chatResponse.data.message.error || 'Chat API failed');
            (err as any).errorCode = chatResponse.data.message.error_code;
            (err as any).sessionId = chatResponse.data.message.session_id;
            throw err;
          } else {
            // Extract text, session_id, title, images, and videoUrl from response
            const responseText = chatResponse.data?.message?.response;
            const sessionId = chatResponse.data?.message?.session_id;
            const title = chatResponse.data?.message?.title;
            const images = chatResponse.data?.message?.images || [];
            const videoUrl = chatResponse.data?.message?.videoUrl;
            response = {
              data: { response: responseText || '', images, videoUrl },
              error: null
            };

            // Update session_id in URL if received
            if (sessionId && sessionId !== chatSessionId) {
              const isNewSession = !chatSessionId;
              setChatSessionId(sessionId);
              setSearchParams({ session_id: sessionId }, { replace: true });
              if (isNewSession) {
                triggerNewChatAdded({
                  sessionId: sessionId,
                  sessionTitle: title || 'New Chat',
                  category: 'General',
                  startedAt: new Date().toISOString(),
                });
              }
            }

            // Update chat title if received
            if (title) {
              setChatTitle(title);
            }
          }
        } catch (err: any) {
          // Handle specific error codes
          const errorCode = err.errorCode;
          if (errorCode === 'SESSION_IMAGE_LIMIT') {
            setChatMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: 'This chat session has reached its image generation limit. Please start a new chat to continue.', isComplete: true }
                : msg
            ));
            setIsTyping(false);
            toast.error('Session image limit reached', {
              description: 'Start a new chat to continue.',
            });
            return;
          } else if (errorCode === 'QUOTA_EXCEEDED') {
            const quotaMsg = err.message || 'You have reached your usage quota. Please try again later.';
            setChatMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: quotaMsg, isComplete: true }
                : msg
            ));
            setIsTyping(false);
            toast.error('Quota exceeded', { description: quotaMsg });
            return;
          } else if (errorCode === 'LLM_KEYS_MISSING') {
            // Surface the backend's explicit key message, mirroring the
            // stream path / desktop instead of the generic fallback.
            const keysMsg = err.message || 'AI chat is unavailable - no API key configured. Ask an administrator to set it in KB Settings → API Keys.';
            setChatMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: keysMsg, isComplete: true }
                : msg
            ));
            setIsTyping(false);
            toast.error('AI chat unavailable', { description: keysMsg });
            return;
          }
          response = { data: null, error: { message: err.message || 'Failed to get response' } };
        }
      }
      if (response.error) {
        logger.error('Chat error:', response.error?.message || 'Unknown error');
        // Update placeholder with error message
        setChatMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isComplete: true }
            : msg
        ));
        setIsTyping(false);
        toast.error('Failed to send message: ' + (response.error.message || 'Unknown error'));
        return;
      }
      
      // Update assistant message placeholder with response
      let messageUpdate: Partial<typeof assistantPlaceholder> & { content: string; isComplete: boolean };

      {
        // Handle regular response from ERP chat API
        const aiResponse = response.data?.response || '';
        const functionResults = response.data?.functionResults || null;
        const responseImages = response.data?.images || [];
        const responseVideoUrl = response.data?.videoUrl;


        // Build the complete message
        let completeMessage = aiResponse;

        // If we have function results, format and append them
        if (functionResults && functionResults.length > 0) {

          for (const funcResult of functionResults) {
            if (funcResult.error) {
              logger.error(`❌ Function ${funcResult.tool} failed:`, funcResult.error);
              continue;
            }

            // Enhanced function result formatting with web_research support
            let formattedResult = '';

            if (funcResult.tool === 'web_research') {
              // Enhanced web research result extraction

              if (funcResult.result?.success && funcResult.result?.data?.full_response) {
                formattedResult = funcResult.result.data.full_response;
              } else if (funcResult.result?.data?.full_response) {
                formattedResult = funcResult.result.data.full_response;
              } else if (funcResult.result?.full_response) {
                formattedResult = funcResult.result.full_response;
              } else if (funcResult.result?.data?.brief_summary) {
                formattedResult = funcResult.result.data.brief_summary;
              } else {
                logger.warn('❌ Mobile - Could not extract web research result');
                formattedResult = 'Web search completed but results could not be displayed properly.';
              }
            } else {
              // Standard result extraction for other tools
              if (funcResult.result?.output) {
                formattedResult = funcResult.result.output;
              } else if (funcResult.result?.data?.full_response) {
                formattedResult = funcResult.result.data.full_response;
              } else if (typeof funcResult.result === 'string') {
                formattedResult = funcResult.result;
              } else if (funcResult.result?.data?.output) {
                formattedResult = funcResult.result.data.output;
              }
            }

            // Append formatted result to message
            if (formattedResult && formattedResult !== 'No results found.') {
              if (completeMessage && !completeMessage.includes(formattedResult)) {
                completeMessage += '\n\n';
              }
              if (!completeMessage.includes(formattedResult)) {
                completeMessage += formattedResult;
              }
            }
          }
        }

        messageUpdate = {
          content: completeMessage || 'I received your message but had trouble processing it.',
          images: responseImages.length > 0 ? responseImages : undefined,
          videoUrl: responseVideoUrl || undefined,
          isComplete: true
        };
      }

      // Update the placeholder message with the response (like desktop)
      setChatMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, ...messageUpdate }
          : msg
      ));
      
      // Clear typing indicator
      setIsTyping(false);
      
      
    } catch (error) {
      logger.error('Error sending message:', error instanceof Error ? error.message : 'Unknown error');
      // Update placeholder with error message
      setChatMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isComplete: true }
          : msg
      ));
      setIsTyping(false);
      toast.error('Failed to send message: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Feature handlers from desktop version
  const toggleKnowledgeMode = () => {
    setIsKnowledgeMode(!isKnowledgeMode);
  };

  // Keep micBlocked in sync with the live browser permission state so the
  // mic button reflects the real state without requiring a click first.
  useEffect(() => {
    let status: any;
    let cancelled = false;
    const handleChange = () => {
      if (!cancelled && status) setMicBlocked(status.state === 'denied');
    };
    (async () => {
      try {
        const perms = (navigator as any).permissions;
        if (!perms?.query) return;
        status = await perms.query({ name: 'microphone' as PermissionName });
        if (cancelled) return;
        setMicBlocked(status.state === 'denied');
        status.addEventListener?.('change', handleChange);
      } catch {
        // Permissions API unavailable - ignore.
      }
    })();
    return () => {
      cancelled = true;
      status?.removeEventListener?.('change', handleChange);
    };
  }, []);

  // Add dictation functionality (Web Speech API) like desktop
  const showMicBlockedToast = () => {
    toast.error('Microphone is blocked', {
      description:
        'Open your browser site settings (lock/info icon in the address bar), set Microphone to Allow, then reload.',
      duration: 8000,
      classNames: {
        description: '!text-gray-700',
      },
    });
  };

  const toggleDictation = async () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition is not supported in this browser. Use voice recording instead.');
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {
        // ignore
      }
      setIsListening(false);
      return;
    }

    // Pre-flight permission check so we don't fail silently after "Never allow".
    try {
      const perms = (navigator as any).permissions;
      if (perms?.query) {
        const status = await perms.query({ name: 'microphone' as PermissionName });
        if (status.state === 'denied') {
          setMicBlocked(true);
          showMicBlockedToast();
          return;
        }
        setMicBlocked(false);
      }
    } catch {
      // Permissions API unavailable (e.g. some Safari versions) - fall through.
    }

    try {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setMicBlocked(false);
      };

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (setChatInputValue.current) {
          const currentValue = setChatInputValue.current.get();
          const newValue = currentValue ? `${currentValue} ${transcript}` : transcript;
          setChatInputValue.current.set(newValue);
        }
        // Focus textarea after speech recognition
        setTimeout(() => {
          const textarea = document.querySelector('textarea');
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          }
        }, 100);
      };

      recognitionRef.current.onerror = (event: any) => {
        logger.error('Speech recognition error:', event.error);
        setIsListening(false);
        switch (event.error) {
          case 'not-allowed':
          case 'service-not-allowed':
            setMicBlocked(true);
            showMicBlockedToast();
            break;
          case 'audio-capture':
            toast.error('No microphone detected on this device.');
            break;
          case 'no-speech':
            toast.error("Didn't catch that - try speaking again.");
            break;
          case 'aborted':
            // user-initiated, stay silent
            break;
          default:
            toast.error('Speech recognition failed. Try voice recording instead.');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.start();
    } catch (error) {
      logger.error('Speech recognition setup error:', error);
      setIsListening(false);
      toast.error('Speech recognition not available. Use voice recording instead.');
    }
  };

  const handleAddAction = () => {
    // Trigger file picker
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        event.target.value = '';
        return;
      }
      
      // Replace with single file (only one image allowed)
      setSelectedFiles([file]);
      toast.success(`File "${file.name}" selected`);
      
      // Focus the textarea after file selection
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
      
      // Reset the input so the same file can be selected again if needed
      event.target.value = '';
    }
  };

  // Generate chat title from messages (like desktop version)
  const generateChatTitle = (messages: Array<{content: string; role: string}>) => {
    if (messages.length === 0) return 'New Chat';
    const firstUserMessage = messages.find(msg => msg.role === 'user')?.content || 'New Chat';
    return firstUserMessage.length > 50 ? firstUserMessage.substring(0, 50) + '...' : firstUserMessage;
  };

  // Save chat session to localStorage (like desktop version)
  const saveChatSession = () => {
    if (chatMessages.length === 0) {
      toast.error('No messages to save');
      return;
    }
    
    try {
      const title = currentChatTitle || generateChatTitle(chatMessages);
      const chatSession = {
        title,
        messages: chatMessages,
        timestamp: new Date().toISOString(),
        role: getCurrentRole(),
        name: getCurrentUserName()
      };
      
      // Save to localStorage like desktop
      const existingSessions = JSON.parse(localStorage.getItem('chat_sessions') || '[]');
      existingSessions.push(chatSession);
      localStorage.setItem('chat_sessions', JSON.stringify(existingSessions));
      
      setCurrentChatTitle(title);
      toast.success('Chat saved successfully');
    } catch (error) {
      logger.error('Error saving chat:', error);
      toast.error('Failed to save chat');
    }
  };

  // Submit or toggle thumbs up/down feedback on an assistant message
  const handleFeedback = async (messageId: string, msgIdx: number, feedbackType: 'thumbs_up' | 'thumbs_down') => {
    if (!chatSessionId) return;
    const assistantIndex = chatMessages
      .slice(0, msgIdx + 1)
      .filter(m => m.role === 'assistant')
      .length - 1;
    const targetMessage = chatMessages.find(m => m.id === messageId);
    const currentFeedback = targetMessage?.feedbackGiven;
    try {
      // Clicking the same feedback button again removes the feedback (toggle off)
      if (currentFeedback === feedbackType) {
        await axios.post(getApiEndpoint('REMOVE_CHAT_FEEDBACK'), {
          session_id: chatSessionId,
          message_index: assistantIndex,
        });
        setChatMessages(prev =>
          prev.map(m => m.id === messageId ? { ...m, feedbackGiven: null } : m)
        );
        toast.success('Feedback removed');
        return;
      }

      await axios.post(getApiEndpoint('SUBMIT_CHAT_FEEDBACK'), {
        session_id: chatSessionId,
        message_index: assistantIndex,
        feedback_type: feedbackType,
        query: chatMessages.filter(m => m.role === 'user').slice(-1)[0]?.content?.slice(0, 500) || '',
        response_preview: (targetMessage?.content || '').slice(0, 500),
      });
      setChatMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, feedbackGiven: feedbackType } : m)
      );
      // Same wording either way (mirrors desktop Interface.tsx).
      toast.success('Feedback recorded');
    } catch (err) {
      logger.error('Failed to submit feedback', err);
      toast.error('Failed to submit feedback');
    }
  };

  // Handle title edit start
  const handleStartEditTitle = () => {
    setEditedTitle(chatTitle);
    setIsEditingTitle(true);
  };

  // Handle title save
  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || !chatSessionId) {
      setIsEditingTitle(false);
      return;
    }

    try {
      const response = await axios.post(
        getApiEndpoint('UPDATE_CHAT_SESSION_TITLE'),
        { session_id: chatSessionId, session_title: editedTitle.trim() },
        { withCredentials: true }
      );

      if (response.data?.message?.success) {
        setChatTitle(editedTitle.trim());
        toast.success('Chat title updated');
      } else {
        toast.error('Failed to update title');
      }
    } catch (error) {
      logger.error('Error updating chat title:', error);
      toast.error('Failed to update title');
    } finally {
      setIsEditingTitle(false);
    }
  };

  // Handle title edit cancel
  const handleCancelEditTitle = () => {
    setEditedTitle('');
    setIsEditingTitle(false);
  };

  // Handle title edit key press (Enter to save, Escape to cancel)
  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  // Start new chat (like desktop version)
  const startNewChat = () => {
    setChatMessages([]);
    setCurrentChatTitle('');
    setIsChatMode(false);
    setIsKnowledgeMode(false); // Turn off Knowledge Base when starting new chat
    setIsTyping(false);
    setSelectedFiles([]);
    // Clear input
    if (setChatInputValue.current) {
      setChatInputValue.current.set('');
    }
    // Clear session_id from state and URL
    setChatSessionId(null);
    setChatTitle('New Chat');
    setSearchParams({}, { replace: true });
    toast.success('Started new chat');
  };

  // Handle new chat click with confirmation (like desktop version)
  const handleNewChatClick = () => {
    if (chatMessages.length > 0) {
      setShowNewChatDialog(true);
    } else {
      startNewChat();
    }
  };

  // Update section based on current route
  useEffect(() => {
    const path = location.pathname;

    if (path === '/dashboard/knowledge') {
      setActiveSection('knowledge');
    } else if (path.startsWith('/dashboard/admin/users')) {
      setActiveSection('dashboard/admin/users');
    } else if (path.startsWith('/dashboard/admin/knowledge-base')) {
      setActiveSection('admin/knowledge-base');
    } else if (path.startsWith('/dashboard/admin/knowledge')) {
      setActiveSection('dashboard/admin/knowledge');
    } else if (path.startsWith('/dashboard/admin/company')) {
      setActiveSection('admin/company');
    } else if (path.startsWith('/dashboard/admin/upload')) {
      setActiveSection('admin/upload');
    } else if (path.startsWith('/dashboard/admin/cloud-import')) {
      setActiveSection('admin/cloud-import');
    } else if (path.startsWith('/dashboard/admin/review-queue')) {
      setActiveSection('admin/review-queue');
    } else if (path.startsWith('/dashboard/admin/entity-types')) {
      setActiveSection('admin/entity-types');
    } else if (path.startsWith('/dashboard/admin/entity-qa')) {
      setActiveSection('admin/entity-qa');
    } else if (path.startsWith('/dashboard/admin')) {
      setActiveSection('dashboard/admin');
    } else if (path.startsWith('/dashboard/finance')) {
      setActiveSection('finance');
    } else if (path.startsWith('/dashboard/vendor')) {
      setActiveSection('vendor');
    } else if (path.startsWith('/dashboard/field')) {
      setActiveSection('field');
    } else {
      setActiveSection('dashboard');
    }
  }, [location.pathname]);

  // Helper function to determine if we're in full-screen mode
  const isFullScreenMode = () => {
    return activeSection === 'knowledge' ||
           activeSection === 'dashboard/knowledge' ||
           // Sales/CRM sections
           activeSection === 'sales' ||
           activeSection === 'sales/crm' ||
           activeSection === 'crm' ||
           // Finance sections
           activeSection === 'finance' ||
           // Vendor sections
           activeSection === 'vendor' ||
           // Field sections
           activeSection === 'field' ||
           // Admin sections
           activeSection === 'dashboard/admin/users' ||
           activeSection === 'dashboard/admin/knowledge' ||
           activeSection === 'admin/upload' ||
           activeSection === 'admin/cloud-import' ||
           activeSection === 'admin/knowledge-base' ||
           activeSection === 'admin/review-queue' ||
           activeSection === 'admin/entity-types' ||
           activeSection === 'admin/entity-qa' ||
           activeSection === 'admin/users' ||
           activeSection === 'admin/company' ||
           // Settings sections
           activeSection === 'settings';
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setShowNavigation(false);

    // Navigate to appropriate route using centralized moduleMapping
    const route = getMobileRouteForSection(section);
    navigate(route);
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'dashboard/knowledge':
        return <MobileKnowledge />;
      case 'knowledge':
        return <MobileKnowledge />;
      case 'finance':
        return <MobileFinance />;
      case 'vendor':
        return <MobileVendorPortal />;
      case 'field':
        return <MobileFieldOperations />;
      case 'dashboard/admin/users':
        return <MobileUserManagement />;
      case 'dashboard/admin/knowledge':
        return <MobileKnowledgeAdmin />;
      case 'admin/users':
        return <MobileUserManagement />;
      case 'admin/knowledge':
        return <MobileKnowledgeAdmin />;
      case 'admin/company':
        return <MobileCompanyBranding />;
      case 'admin/upload':
        return <KBUploadPage />;
      case 'admin/cloud-import':
        return <KBCloudImportPage />;
case 'admin/review-queue':
        return <KBReviewQueuePage />;
      case 'admin/knowledge-base':
        return <KBDocumentsPage />;
      case 'admin/entity-types':
        return <KBEntityTypesPage />;
      case 'admin/entity-qa':
        return <KBEntityQAPage />;
      case 'settings':
        return <MobileUserProfile />;
      default:
        return (
          <MobileCentralInterface
            currentRole={getCurrentRole()}
            activeAgent={activeAgent}
            onChatSubmit={handleChatSubmit}
            isUserTyping={isUserTyping}
            isChatMode={isChatMode}
            chatMessages={chatMessages}
            isTyping={isTyping}
            currentUserName={getCurrentUserName()}
            chatTitle={chatTitle}
            chatSessionId={chatSessionId}
            isEditingTitle={isEditingTitle}
            editedTitle={editedTitle}
            onEditedTitleChange={setEditedTitle}
            onStartEditTitle={handleStartEditTitle}
            onSaveTitle={handleSaveTitle}
            onCancelEditTitle={handleCancelEditTitle}
            onTitleKeyPress={handleTitleKeyPress}
            onNewChat={startNewChat}
            isLoadingSession={isLoadingSession}
            onFeedback={handleFeedback}
            isKnowledgeMode={isKnowledgeMode}
            onSaveToKB={(content) => setSaveToKBContent(content)}
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div
        className="dark fixed inset-0 bg-background text-foreground flex flex-col"
        style={{
          height: '100dvh',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        {/* Mobile Header */}
        <MobileHeader
          onMenuClick={() => setShowNavigation(true)}
        />

        {/* Main Content Area - Dark background for dashboard, light for other modules */}
        <div
          className={`flex-1 flex flex-col overflow-hidden ${
            !isFullScreenMode() ? 'pb-24' : ''
          } bg-background`}
        >
          {activeSection === 'dashboard' ? (
            renderSectionContent()
          ) : (
            <div data-scroll-root className="flex-1 overflow-y-auto">
              {renderSectionContent()}
            </div>
          )}
        </div>

        {/* Chat Input - Absolutely positioned at bottom, hidden in full-screen mode */}
        {!isFullScreenMode() && (
          <>
            {/* TEMP: Upload hidden via showPlusButton={false} - remove to re-enable */}
            <MobileChatInput
              onSubmit={handleChatSubmit}
              activeAgent={activeAgent}
              isTyping={isTyping}
              onTypingChange={(typing) => {
                setIsUserTyping(typing);
                setIsInputFocused(typing);
              }}
              setValue={setChatInputValue}
              isKnowledgeMode={isKnowledgeMode}
              isListening={isListening}
              micBlocked={micBlocked}
              selectedFiles={selectedFiles}
              isChatMode={isChatMode}
              onToggleKnowledge={toggleKnowledgeMode}
              onToggleDictation={toggleDictation}
              onAddAction={handleAddAction}
              showPlusButton={false}
            />
          </>
        )}

        {/* Slide-out Navigation Panel */}
        <MobileNavigation 
          isOpen={showNavigation}
          onClose={() => setShowNavigation(false)}
          onSectionChange={handleSectionChange}
          activeSection={activeSection}
        />

        {/* Hidden file input for Plus icon functionality - single image only */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
          multiple={false}
        />

        {/* Mobile New Chat Confirmation Dialog */}
        {showNewChatDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl border border-border shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-foreground mb-3">Start New Chat?</h3>
              <p className="text-muted-foreground text-sm mb-6">
                You have unsaved messages. Do you want to save this chat before starting a new one?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    saveChatSession();
                    startNewChat();
                    setShowNewChatDialog(false);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Save & New
                </button>
                <button
                  onClick={() => {
                    startNewChat();
                    setShowNewChatDialog(false);
                  }}
                  className="flex-1 bg-secondary hover:bg-secondary/85 text-secondary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Don't Save
                </button>
                <button
                  onClick={() => setShowNewChatDialog(false)}
                  className="px-4 py-2 text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <SaveToKBModal
        open={!!saveToKBContent}
        onClose={() => setSaveToKBContent(null)}
        content={saveToKBContent || ''}
        onSuccess={() => {
          toast.success('Saved to Knowledge Base');
        }}
      />
    </ErrorBoundary>
  );
};